<template>
  <BaseModal
    :model-value="modelValue"
    size="lg"
    :show-close="false"
    :close-on-overlay="false"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="onboarding-modal">
      <!-- Progress indicator -->
      <div class="progress-bar">
        <div class="progress-steps">
          <div
            v-for="s in 3"
            :key="s"
            class="progress-step"
            :class="{ active: s <= step, completed: s < step }"
          >
            <span class="step-number">{{ s }}</span>
          </div>
        </div>
        <div class="progress-line">
          <div class="progress-fill" :style="{ width: `${((step - 1) / 2) * 100}%` }"></div>
        </div>
      </div>

      <!-- Step content -->
      <div class="step-container">
        <OnboardingStep1
          v-if="step === 1"
          :initial-data="step1Data"
          @update="step1Data = $event"
        />
        <OnboardingStep2
          v-else-if="step === 2"
          :initial-data="step2Data"
          @update="step2Data = $event"
        />
        <OnboardingStep3
          v-else
          :initial-data="step3Data"
          @update="step3Data = $event"
        />
      </div>

      <!-- Navigation buttons -->
      <div class="navigation">
        <button
          v-if="step > 1"
          class="btn btn-secondary"
          @click="prevStep"
        >
          Back
        </button>
        <div class="spacer"></div>
        <button
          v-if="step < 3"
          class="btn btn-primary"
          :disabled="!canProceed"
          @click="nextStep"
        >
          Continue
        </button>
        <button
          v-else
          class="btn btn-primary"
          :disabled="!canComplete || loading"
          @click="complete"
        >
          <LoadingSpinner v-if="loading" size="sm" />
          <span v-else>Complete Setup</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import BaseModal from '@/components/common/BaseModal.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import OnboardingStep1 from './OnboardingStep1.vue'
import OnboardingStep2 from './OnboardingStep2.vue'
import OnboardingStep3 from './OnboardingStep3.vue'

const props = defineProps<{
  modelValue: boolean
}>()

defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const store = useStore()

const step = computed(() => store.state.onboardingStep)
const loading = computed(() => store.state.isLoading)

// Step data
const step1Data = ref({
  businessUrl: '',
  aboutBusiness: '',
})

const step2Data = ref({
  fieldsOfInterest: [] as string[],
  keywordsToMonitor: [] as string[],
})

const step3Data = ref({
  name: '',
  toneOfVoice: '',
  styleGuide: '',
  speechPatternGuidelines: '',
})

// Validation
const canProceed = computed(() => {
  if (step.value === 1) {
    return step1Data.value.aboutBusiness.trim().length > 0
  }
  if (step.value === 2) {
    return step2Data.value.fieldsOfInterest.length > 0
  }
  return true
})

const canComplete = computed(() => {
  return (
    step3Data.value.name.trim().length > 0 &&
    step3Data.value.toneOfVoice.trim().length > 0 &&
    step3Data.value.styleGuide.trim().length > 0
  )
})

// Navigation
const nextStep = async () => {
  if (step.value === 2) {
    // Save steps 1 and 2 data
    await store.dispatch('completeOnboarding', {
      about_business: step1Data.value.aboutBusiness,
      business_url: step1Data.value.businessUrl || undefined,
      fields_of_interest: step2Data.value.fieldsOfInterest,
      keywords_to_monitor: step2Data.value.keywordsToMonitor,
    })
  } else {
    store.commit('SET_ONBOARDING_STEP', step.value + 1)
  }
}

const prevStep = () => {
  store.commit('SET_ONBOARDING_STEP', step.value - 1)
}

const complete = async () => {
  await store.dispatch('completeOnboardingWriter', {
    name: step3Data.value.name,
    tone_of_voice: step3Data.value.toneOfVoice,
    style_guide: step3Data.value.styleGuide,
    speech_pattern_guidelines: step3Data.value.speechPatternGuidelines || undefined,
  })
}

// Prefill data from user when modal opens (step is determined by checkOnboarding action)
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    const user = store.state.user
    if (user) {
      // Prefill step 1 data from existing user data
      step1Data.value = {
        businessUrl: '', // Not stored on user, only used for parsing
        aboutBusiness: user.about_business || '',
      }
      // Prefill step 2 data from existing user data
      step2Data.value = {
        fieldsOfInterest: user.fields_of_interest || [],
        keywordsToMonitor: user.keywords_to_monitor || [],
      }
    }
    // Note: Step is determined by checkOnboarding action based on existing user data
  }
})
</script>

<style scoped>
.onboarding-modal {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  min-height: 0;
  max-height: calc(100vh - var(--space-8) - var(--space-10));
}

.progress-bar {
  position: relative;
  padding: 0 var(--space-8);
  flex-shrink: 0;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.progress-step {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--surface-elevated);
  border: 2px solid var(--border-default);
  border-radius: 50%;
  transition: all var(--transition-normal);
}

.progress-step.active {
  background: var(--color-primary-500);
  border-color: var(--color-primary-500);
}

.progress-step.completed {
  background: var(--color-primary-500);
  border-color: var(--color-primary-500);
}

.step-number {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--text-tertiary);
}

.progress-step.active .step-number,
.progress-step.completed .step-number {
  color: white;
}

.progress-line {
  position: absolute;
  top: 50%;
  left: var(--space-8);
  right: var(--space-8);
  height: 2px;
  background: var(--border-default);
  transform: translateY(-50%);
  margin-left: 16px;
  margin-right: 16px;
}

.progress-fill {
  height: 100%;
  background: var(--color-primary-500);
  transition: width var(--transition-normal);
}

.step-container {
  min-height: 320px;
  flex: 1;
  overflow-y: auto;
}

.navigation {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.spacer {
  flex: 1;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-width: 100px;
  padding: var(--space-3) var(--space-5);
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--surface-elevated);
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--surface-hover);
}

.btn-primary {
  background: var(--color-primary-500);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-600);
}
</style>
